import React from 'react';
import { useReplacePaymentMethodService } from '@atlassian/commerce-ui/payment-methods';
import { useCCPCreditCardFormStateGenerateThenUpdateCard } from './use-ccp-state-generate-and-update-card';
import { OnUpdateCallbacks, PaymentMethodCreationStage, PaymentMethodSuccessResult } from './types';
import { usePaymentUpdateNotification } from './use-payment-update-notification';
import { useAddPaymentMethod } from './use-add-payment-method';

type UseReplacePaymentMethodArgs = {
  txaId: string;
  idOfPaymentMethodBeingReplaced: string;
} & OnUpdateCallbacks;

type UseReplacePaymentMethod = (
  args: UseReplacePaymentMethodArgs
) => {
  creationStage: PaymentMethodCreationStage;
  generatePaymentMethod: () => Promise<PaymentMethodSuccessResult | undefined>;
  updateCard: () => Promise<void>;
  parameterizedUpdatePaymentMethod?: (stripeId: string, newPaymentMethodId: string) => Promise<void>;
  isWarningBeingShown: boolean;
  closeWarning: () => void;
  openWarning: () => void;
  isPaypalOperationInProgress: boolean;
  updatePaypal?: (txaId: string, result: any) => void;
};

export const useReplacePaymentMethod: UseReplacePaymentMethod = ({
  txaId,
  idOfPaymentMethodBeingReplaced,
  onUpdateSuccess,
  onUpdateFailure,
  onUpdateDone,
  successMessage,
  failureMessage,
}) => {
  const { update: replaceWith, error: replacementError, loading: replacementLoading } = useReplacePaymentMethodService(
    txaId,
    idOfPaymentMethodBeingReplaced
  );

  const { notifySuccess, notifyFailure } = usePaymentUpdateNotification('edit');

  const {
    creationStage,
    generatePaymentMethod,
    updateCard,
    isWarningBeingShown,
    closeWarning,
    openWarning,
  } = useCCPCreditCardFormStateGenerateThenUpdateCard({
    updateMethod: replaceWith,
    onUpdateSuccess: () => {
      notifySuccess(successMessage);
      onUpdateSuccess && onUpdateSuccess();
    },
    onUpdateFailure: () => {
      notifyFailure(failureMessage as string);
      onUpdateFailure && onUpdateFailure();
    },
    onUpdateDone,
  });

  const onPayPalSuccess = async (result: any) => {
    // No need to replace payment method in our system if it's within the same PayPal account (ID remains the same).
    if (result.id === idOfPaymentMethodBeingReplaced) {
      notifySuccess(successMessage);
      onUpdateSuccess && onUpdateSuccess();
      onUpdateDone && onUpdateDone();
      return;
    }

    try {
      await replaceWith(result.id);
      notifySuccess(successMessage);
      onUpdateSuccess && onUpdateSuccess();
      onUpdateDone && onUpdateDone();
    } catch (e) {
      notifyFailure(failureMessage as string);
      onUpdateFailure && onUpdateFailure();
    }

    closeWarning();
  };

  const { onAddPaypalPaymentMethod, isPaypalOperationInProgress } = useAddPaymentMethod({
    overrideOnUpdateSuccess: onPayPalSuccess,
    successMessage,
    failureMessage,
  });

  return {
    creationStage,
    generatePaymentMethod,
    updateCard,
    isWarningBeingShown,
    closeWarning,
    openWarning,
    isPaypalOperationInProgress: isPaypalOperationInProgress || replacementLoading,
    updatePaypal: onAddPaypalPaymentMethod,
  };
};
